import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { PlusIcon, TrashIcon, BuildingIcon, CreditCardIcon, UserIcon, UsersIcon } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

// Kart türü meta bilgileri — uzunluk, format ve görsel etiket.
const CARD_TYPES = [
  { id: 'visa',   label: 'Visa',        length: 16, groups: [4, 4, 4, 4], color: 'bg-blue-600' },
  { id: 'master', label: 'Mastercard',  length: 16, groups: [4, 4, 4, 4], color: 'bg-orange-600' },
  { id: 'amex',   label: 'Amex',        length: 15, groups: [4, 6, 5],    color: 'bg-emerald-600' },
  { id: 'troy',   label: 'Troy',        length: 16, groups: [4, 4, 4, 4], color: 'bg-red-600' },
  { id: 'other',  label: 'Diğer',       length: 16, groups: [4, 4, 4, 4], color: 'bg-gray-600' },
];

const cardTypeMeta = (id) => CARD_TYPES.find((t) => t.id === id) || CARD_TYPES[0];

// Verilen kart türüne göre ham rakamı grupla ve boşluk ekle.
const formatCardNumber = (raw, typeId = 'visa') => {
  const meta = cardTypeMeta(typeId);
  const digits = (raw || '').replace(/\D/g, '').slice(0, meta.length);
  const parts = [];
  let idx = 0;
  for (const g of meta.groups) {
    if (idx >= digits.length) break;
    parts.push(digits.slice(idx, idx + g));
    idx += g;
  }
  return parts.join(' ');
};

const CreditCardsSettings = () => {
  const [tab, setTab] = useState('banks');
  const [banks, setBanks] = useState([]);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modals
  const [showBankModal, setShowBankModal] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);

  // Bank form
  const [bankName, setBankName] = useState('');
  const [savingBank, setSavingBank] = useState(false);

  // Card form
  const [cardName, setCardName] = useState('');
  const [cardType, setCardType] = useState('visa'); // 'visa'|'master'|'amex'|'troy'|'other'
  const [ownerType, setOwnerType] = useState('mine'); // 'mine' | 'other'
  const [ownerName, setOwnerName] = useState('');
  const [cardNumber, setCardNumber] = useState(''); // ekranda formatlanmış hâl
  const [selectedBankId, setSelectedBankId] = useState('');
  const [savingCard, setSavingCard] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([
        axios.get(`${API}/banks`, { headers: authHeader() }),
        axios.get(`${API}/credit-cards`, { headers: authHeader() }),
      ]);
      setBanks(b.data || []);
      setCards(c.data || []);
    } catch (_e) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const resetBankForm = () => { setBankName(''); };
  const resetCardForm = () => {
    setCardName('');
    setCardType('visa');
    setOwnerType('mine');
    setOwnerName('');
    setCardNumber('');
    setSelectedBankId('');
  };

  const submitBank = async () => {
    const name = bankName.trim();
    if (!name) { toast.error('Banka adı boş olamaz'); return; }
    setSavingBank(true);
    try {
      await axios.post(`${API}/banks`, { bank_name: name }, { headers: authHeader() });
      toast.success('Banka eklendi');
      setShowBankModal(false);
      resetBankForm();
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Banka eklenemedi');
    } finally { setSavingBank(false); }
  };

  const submitCard = async () => {
    if (!cardName.trim()) { toast.error('Kart adı boş olamaz'); return; }
    if (ownerType === 'other' && !ownerName.trim()) {
      toast.error('Kart sahibinin adını girin'); return;
    }
    const rawDigits = cardNumber.replace(/\D/g, '');
    const expectedLen = cardTypeMeta(cardType).length;
    if (rawDigits.length !== expectedLen) {
      toast.error(`Kart numarası ${expectedLen} haneli olmalı (${cardTypeMeta(cardType).label})`);
      return;
    }
    setSavingCard(true);
    try {
      await axios.post(`${API}/credit-cards`, {
        card_name: cardName.trim(),
        card_type: cardType,
        owner_type: ownerType,
        owner_name: ownerType === 'other' ? ownerName.trim() : null,
        card_number: rawDigits,
        bank_id: selectedBankId || null,
      }, { headers: authHeader() });
      toast.success('Kart eklendi');
      setShowCardModal(false);
      resetCardForm();
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kart eklenemedi');
    } finally { setSavingCard(false); }
  };

  const deleteBank = async (bank) => {
    if (!window.confirm(`"${bank.bank_name}" bankasını silmek istediğinize emin misiniz?`)) return;
    try {
      await axios.delete(`${API}/banks/${bank.id}`, { headers: authHeader() });
      toast.success('Banka silindi');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Silinemedi');
    }
  };

  const deleteCard = async (card) => {
    if (!window.confirm(`"${card.card_name}" kartını silmek istediğinize emin misiniz?`)) return;
    try {
      await axios.delete(`${API}/credit-cards/${card.id}`, { headers: authHeader() });
      toast.success('Kart silindi');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Silinemedi');
    }
  };

  const bankById = (id) => banks.find((b) => b.id === id);

  return (
    <div className="p-6 space-y-4" data-testid="credit-cards-settings">
      <div className="flex items-center gap-2 mb-2">
        <CreditCardIcon className="w-6 h-6 text-teal-600" />
        <h2 className="text-2xl font-bold text-gray-900">Kredi Kartları</h2>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2 h-11 bg-gray-100">
          <TabsTrigger value="banks" data-testid="cc-tab-banks">
            <BuildingIcon className="w-4 h-4 mr-2" /> Bankalar
            <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
              {banks.length}
            </span>
          </TabsTrigger>
          <TabsTrigger value="cards" data-testid="cc-tab-cards">
            <CreditCardIcon className="w-4 h-4 mr-2" /> Kartlarım
            <span className="ml-2 text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">
              {cards.length}
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="banks" className="mt-4">
          <div className="flex justify-end mb-4">
            <Button
              onClick={() => setShowBankModal(true)}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="cc-add-bank-btn"
            >
              <PlusIcon className="w-4 h-4 mr-2" /> Yeni Banka Ekle
            </Button>
          </div>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Yükleniyor…</div>
          ) : banks.length === 0 ? (
            <Card className="p-10 text-center bg-gray-50 border-dashed">
              <BuildingIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600">Henüz banka eklemediniz</p>
              <p className="text-sm text-gray-400 mt-1">Önce bankalarınızı ekleyin, ardından kart eklerken buradan seçebilirsiniz</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {banks.map((b) => (
                <Card
                  key={b.id}
                  className="group p-4 flex items-center gap-3 hover:border-teal-400 transition"
                  data-testid={`cc-bank-card-${b.id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <BuildingIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">{b.bank_name}</div>
                    <div className="text-xs text-gray-400">{new Date(b.created_at).toLocaleDateString('tr-TR')}</div>
                  </div>
                  <button
                    onClick={() => deleteBank(b)}
                    className="opacity-0 group-hover:opacity-100 p-1.5 rounded hover:bg-red-50 text-red-500 transition"
                    aria-label="Bankayı sil"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="cards" className="mt-4">
          <div className="flex justify-end mb-4">
            <Button
              onClick={() => setShowCardModal(true)}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="cc-add-card-btn"
            >
              <PlusIcon className="w-4 h-4 mr-2" /> Yeni Kart Ekle
            </Button>
          </div>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Yükleniyor…</div>
          ) : cards.length === 0 ? (
            <Card className="p-10 text-center bg-gray-50 border-dashed">
              <CreditCardIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600">Henüz kart eklemediniz</p>
              <p className="text-sm text-gray-400 mt-1">Yeni Kart Ekle butonuyla başlayın — güvenlik için yalnızca son 4 hanesi saklanır</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {cards.map((c) => {
                const bank = bankById(c.bank_id);
                const typeMeta = cardTypeMeta(c.card_type || 'visa');
                return (
                  <Card
                    key={c.id}
                    className="group p-4 relative overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900 text-white"
                    data-testid={`cc-card-${c.id}`}
                  >
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <div className="text-xs text-white/60 uppercase tracking-wider">
                          {c.owner_type === 'mine' ? 'Kendi Kartım' : 'Başkasının Kartı'}
                        </div>
                        <div className="text-lg font-semibold mt-1">{c.card_name}</div>
                      </div>
                      <span
                        className={`text-[10px] font-bold px-2 py-1 rounded ${typeMeta.color} text-white uppercase tracking-wider`}
                      >
                        {typeMeta.label}
                      </span>
                    </div>
                    <div className="text-lg font-mono tracking-wider mb-3">{c.masked_number}</div>
                    <div className="flex items-end justify-between text-xs">
                      <div className="flex items-center gap-1 text-white/70">
                        {c.owner_type === 'mine' ? (
                          <UserIcon className="w-3 h-3" />
                        ) : (
                          <UsersIcon className="w-3 h-3" />
                        )}
                        <span>{c.owner_type === 'mine' ? 'Ben' : c.owner_name}</span>
                      </div>
                      {bank && (
                        <span className="text-white/60">{bank.bank_name}</span>
                      )}
                    </div>
                    <button
                      onClick={() => deleteCard(c)}
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1.5 rounded hover:bg-red-500/20 text-red-300 transition"
                      aria-label="Kartı sil"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Banka ekleme modalı */}
      <Dialog open={showBankModal} onOpenChange={(o) => { setShowBankModal(o); if (!o) resetBankForm(); }}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Yeni Banka Ekle</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="bank-name-input">Banka Adı</Label>
              <Input
                id="bank-name-input"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && bankName.trim()) submitBank(); }}
                placeholder="Örn: Ziraat Bankası"
                autoFocus
                data-testid="cc-bank-name-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowBankModal(false)}>İptal</Button>
              <Button
                onClick={submitBank}
                disabled={savingBank || !bankName.trim()}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="cc-bank-submit"
              >
                {savingBank ? 'Kaydediliyor…' : 'Kaydet'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Kart ekleme modalı */}
      <Dialog open={showCardModal} onOpenChange={(o) => { setShowCardModal(o); if (!o) resetCardForm(); }}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle>Yeni Kart Ekle</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="card-name-input">Kart Adı *</Label>
              <Input
                id="card-name-input"
                value={cardName}
                onChange={(e) => setCardName(e.target.value)}
                placeholder="Örn: İş Bankası Maximum"
                autoFocus
                data-testid="cc-card-name-input"
              />
            </div>

            <div>
              <Label>Kimin Kartı?</Label>
              <div className="mt-1 flex gap-2">
                <button
                  type="button"
                  onClick={() => setOwnerType('mine')}
                  className={`flex-1 h-10 rounded-md border text-sm transition ${
                    ownerType === 'mine'
                      ? 'border-teal-600 bg-teal-50 text-teal-700 font-medium'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  data-testid="cc-owner-mine"
                >
                  <UserIcon className="w-4 h-4 inline mr-1.5" />
                  Kendi Kartım
                </button>
                <button
                  type="button"
                  onClick={() => setOwnerType('other')}
                  className={`flex-1 h-10 rounded-md border text-sm transition ${
                    ownerType === 'other'
                      ? 'border-teal-600 bg-teal-50 text-teal-700 font-medium'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  data-testid="cc-owner-other"
                >
                  <UsersIcon className="w-4 h-4 inline mr-1.5" />
                  Başkasının Kartı
                </button>
              </div>
            </div>

            {ownerType === 'other' && (
              <div>
                <Label htmlFor="owner-name-input">Kart Sahibi Ad Soyad *</Label>
                <Input
                  id="owner-name-input"
                  value={ownerName}
                  onChange={(e) => setOwnerName(e.target.value)}
                  placeholder="Örn: Ali Yılmaz"
                  data-testid="cc-owner-name-input"
                />
              </div>
            )}

            <div>
              <Label>Kart Türü *</Label>
              <div className="mt-1 grid grid-cols-5 gap-2">
                {CARD_TYPES.map((t) => {
                  const active = cardType === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => {
                        setCardType(t.id);
                        // Türü değiştirince mevcut girişi yeni formata çevir + kırp
                        setCardNumber(formatCardNumber(cardNumber, t.id));
                      }}
                      className={`h-9 rounded-md border text-xs font-medium transition ${
                        active
                          ? 'border-teal-600 bg-teal-50 text-teal-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                      data-testid={`cc-card-type-${t.id}`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {cardTypeMeta(cardType).length} haneli
                {cardType === 'amex' ? ' • 4-6-5 format' : ' • 4-4-4-4 format'}
              </p>
            </div>

            <div>
              <Label htmlFor="card-number-input">Kart Numarası *</Label>
              <Input
                id="card-number-input"
                value={cardNumber}
                onChange={(e) => setCardNumber(formatCardNumber(e.target.value, cardType))}
                placeholder={cardType === 'amex' ? '3782 822463 10005' : '1234 5678 9012 3456'}
                inputMode="numeric"
                autoComplete="off"
                className="font-mono tracking-wider"
                data-testid="cc-card-number-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Güvenlik için sunucumuzda yalnızca son 4 hane saklanır.
              </p>
            </div>

            <div>
              <Label htmlFor="card-bank-select">Banka (Opsiyonel)</Label>
              <select
                id="card-bank-select"
                value={selectedBankId}
                onChange={(e) => setSelectedBankId(e.target.value)}
                className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-white"
                data-testid="cc-card-bank-select"
              >
                <option value="">— Banka seçilmedi —</option>
                {banks.map((b) => (
                  <option key={b.id} value={b.id}>{b.bank_name}</option>
                ))}
              </select>
              {banks.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  Banka listesi boş — önce &quot;Bankalar&quot; sekmesinden ekleyebilirsiniz.
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowCardModal(false)}>İptal</Button>
              <Button
                onClick={submitCard}
                disabled={
                  savingCard ||
                  !cardName.trim() ||
                  cardNumber.replace(/\D/g, '').length !== cardTypeMeta(cardType).length
                }
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="cc-card-submit"
              >
                {savingCard ? 'Kaydediliyor…' : 'Kaydet'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreditCardsSettings;
